import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Avatar,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  SwapHoriz as TransferIcon,
  ShoppingCart as SaleIcon,
  Build as AdjustmentIcon,
  History as HistoryIcon,
  Clear as ClearIcon,
  Create as CreateIcon,
  Edit as UpdateIcon,
  Delete as DeleteIcon,
  Category as ProductIcon,
  TableChart as LookupIcon,
  FilterList as FilterIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  } from '@mui/icons-material';
import { activities as activitiesAPI, sales } from '../services/api';
import { useAuth } from '../services/auth';

function ActivityLog() {
  // Helper function to format UTC timestamp to Manila time using manual offset
  const formatToManila = (utcString, dateFormat = {}, timeFormat = {}) => {
    if (!utcString) return null;

    // Parse the UTC timestamp
    const utcDate = new Date(utcString);
    if (isNaN(utcDate.getTime())) {
      console.error('Invalid date:', utcString);
      return null;
    }

    // Convert UTC to Manila time (UTC+8) by adding 8 hours
    const manilaOffset = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
    const manilaDate = new Date(utcDate.getTime() + manilaOffset);

    // Format the Manila time
    const dateOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      ...dateFormat
    };

    const timeOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...timeFormat
    };

    return {
      date: manilaDate.toLocaleDateString('en-US', dateOptions),
      time: manilaDate.toLocaleTimeString('en-US', timeOptions)
    };
  };

  // Helper function to format timestamps that are already in Manila time
  const formatManilaTime = (timeString) => {
    if (!timeString) return { date: null, time: null };

    try {
      // Backend sends Manila time as string like "2025-10-25T23:05:38-08:00"
      // The time should already be Manila time, just parse and format it
      if (timeString.includes('-08:00') || timeString.includes('+08:00')) {
        const datePart = timeString.split('T')[0]; // "2025-10-25"
        const timePart = timeString.split('T')[1].split('-')[0]; // "23:05:38"

        const [year, month, day] = datePart.split('-');
        const [hour, minute] = timePart.split(':');

        // Create date object using the parsed parts
        const parsedDate = new Date(year, parseInt(month) - 1, day);

        // Format date
        const dateString = parsedDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit'
        });

        // Convert 24-hour to 12-hour format
        const hour24 = parseInt(hour);
        const isAM = hour24 < 12;
        const displayHour = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = isAM ? 'AM' : 'PM';
        const formattedTime = `${displayHour.toString().padStart(2, '0')}:${minute} ${ampm}`;

        return {
          date: dateString,
          time: formattedTime
        };
      }
    } catch (e) {
      console.error('Error parsing Manila time:', e, 'for timeString:', timeString);
    }

    return { date: null, time: null };
  };

  // Helper function to truncate text with ellipsis
  const truncateText = (text, maxLength = 30) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Component to display truncated text with click to view full text
  const TruncatedText = ({ text, maxLength = 30, title = 'Full Text' }) => {
    const truncated = truncateText(text, maxLength);
    const isTruncated = text && text.length > maxLength;

    if (!isTruncated) {
      return <span>{text}</span>;
    }

    return (
      <span
        onClick={() => setTextModal({
          open: true,
          title: title,
          text: text
        })}
        style={{
          cursor: 'pointer',
          color: 'inherit'
        }}
        title="Click to view full text"
      >
        {truncated}
      </span>
    );
  };

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [activityType, setActivityType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  // Auth and refund dialog state
  const { canRefundSales } = useAuth();
  // Temporarily disable refund dialog
  const [refundDialog, setRefundDialog] = useState({
    open: false,
    saleId: null,
    refundType: 'REFUND',
    reason: '',
    loading: false
  });

  const [successModal, setSuccessModal] = useState({
    open: false,
    message: ''
  });

  const [errorModal, setErrorModal] = useState({
    open: false,
    title: '',
    message: ''
  });

  const [infoModal, setInfoModal] = useState({
    open: false,
    title: '',
    message: ''
  });

  const [textModal, setTextModal] = useState({
    open: false,
    title: '',
    text: ''
  });

  useEffect(() => {
    fetchActivities();
  }, [page, rowsPerPage, activityType, searchTerm]);

  const fetchActivities = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        limit: rowsPerPage.toString(),
        offset: (page * rowsPerPage).toString(),
      });

      if (activityType) {
        params.append('activity_type', activityType);
      }

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      
      // Convert URLSearchParams to object for API calls
      const paramsObj = Object.fromEntries(params.entries());

      // Fetch stock movements (always available) and try activity logs (may not be available yet)
      const stockResponse = await activitiesAPI.getStockMovements(paramsObj);
      let logsResponse;

      try {
        logsResponse = await activitiesAPI.getLogs(paramsObj);
      } catch (err) {
        console.warn('Could not fetch activity logs:', err);
        logsResponse = { data: null };
      }

      const stockData = stockResponse.data;
      let logsData = { Activities: [], Total: 0 };

      
      // Handle the case where activity logs endpoint might not be available yet
      if (logsResponse.data) {
        const responseData = logsResponse.data;
        // Check if response contains an error
        if (responseData.Error) {
          console.warn('Activity logs endpoint returned error:', responseData.Error);
          logsData = { Activities: [], Total: 0 };
        } else {
          logsData = responseData;
        }
      } else {
        console.warn('Activity logs endpoint returned error, using stock movements only');
        logsData = { Activities: [], Total: 0 };
      }

      // Combine and sort activities by date
      const stockMovements = (stockData.Movements || []).map(movement => ({
        ...movement,
        Type: 'STOCK_MOVEMENT',
        DisplayTitle: `${movement.BrandName || ''} ${movement.ProductModel || ''}`.trim(),
        DisplayDescription: getMovementLabel(movement.MovementType),
        ActivityType: movement.MovementType,
        EntityType: 'STOCK'
      }));

      const generalLogs = (logsData.Activities || []).map(log => ({
        ...log,
        Type: 'ACTIVITY_LOG',
        DisplayTitle: log.EntityName || 'System Activity',
        DisplayDescription: log.ActionDescription,
        MovementType: log.ActivityType // For consistent filtering
      }));

      let allActivities = [...stockMovements, ...generalLogs];

      // Client-side filtering by search term if provided
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase().trim();
        allActivities = allActivities.filter(activity => {
          const displayTitle = (activity.DisplayTitle || '').toLowerCase();
          const displayDescription = (activity.DisplayDescription || '').toLowerCase();
          const notes = (activity.Notes || activity.ActionDescription || '').toLowerCase();
          const brandName = (activity.BrandName || '').toLowerCase();
          const productModel = (activity.ProductModel || '').toLowerCase();

          return displayTitle.includes(searchLower) ||
                 displayDescription.includes(searchLower) ||
                 notes.includes(searchLower) ||
                 brandName.includes(searchLower) ||
                 productModel.includes(searchLower);
        });
      }

      allActivities.sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));

      // Debug: Log activities to see their structure
      if (process.env.NODE_ENV === 'development') {
        try {
          console.log('Activities count:', allActivities.length);
          const salesActivities = allActivities.filter(a => a.MovementType === 'SALE');
          console.log('Sales activities found:', salesActivities.length);
          console.log('User can refund:', canRefundSales());
        } catch (err) {
          console.error('Debug logging error:', err);
        }
      }

      setActivities(allActivities);
      setTotal(searchTerm.trim() ? allActivities.length : (stockData.Total || 0) + (logsData.Total || 0));

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setActivityType('');
    setSearchTerm('');
    setPage(0);
  };

  const toggleRowExpansion = (activityId) => {
    setExpandedRows(prev => ({
      ...prev,
      [activityId]: !prev[activityId]
    }));
  };

  const handleRefundClick = async (activity) => {
    console.log('Refund button clicked for activity:', activity);
    console.log('activity.MovementType:', activity.MovementType);
    console.log('activity.Reference:', activity.Reference);
    console.log('activity.Type:', activity.Type);

    // Check if already refunded
    if (isAlreadyRefunded(activity)) {
      setErrorModal({
        open: true,
        title: 'Already Refunded',
        message: 'This sale has already been refunded or marked as defective.'
      });
      return;
    }

    let saleId;

    // Handle different types of sale activities
    if (activity.Reference && activity.Reference.toLowerCase().includes('sale')) {
      if (activity.Reference.startsWith('SALE-')) {
        // Activity log entries have full UUID: "SALE-9a52c755-73d4-463e-aea6-ef843fe63d27"
        saleId = activity.Reference.replace('SALE-', '');
        console.log('Found sale ID from activity log:', saleId);
      } else if (activity.Reference.includes('#')) {
        // Stock movement entries have truncated reference: "Sale #9a52c755"
        try {
          console.log('Looking up sale ID for reference:', activity.Reference);
          const response = await sales.findByReference(activity.Reference);
          saleId = response.data.SaleID;
          console.log('Found sale ID via API:', saleId);
        } catch (error) {
          console.error('Failed to find sale ID:', error);
          const errorMsg = error.response?.data?.Error || error.message;
          setErrorModal({
            open: true,
            title: 'Error',
            message: `Unable to find sale: ${errorMsg}`
          });
          return;
        }
      } else {
        saleId = activity.Reference;
        console.log('Using reference as sale ID:', saleId);
      }
    }
    // Fallback
    else {
      console.log('No recognizable sale reference found');
      setInfoModal({
        open: true,
        title: 'Cannot Refund',
        message: 'This activity does not appear to be a sale that can be refunded.'
      });
      return;
    }

    console.log('Final extracted saleId:', saleId);

    // Since the API lookup worked, we trust the returned sale ID format
    // Just do basic validation: non-empty and reasonable length
    const cleanSaleId = saleId.trim();
    if (!cleanSaleId || cleanSaleId.length < 10) {
      console.error('Invalid sale ID extracted:', cleanSaleId);
      setInfoModal({
        open: true,
        title: 'Cannot Refund',
        message: 'Unable to extract valid sale ID from activity. This may not be a refundable sale.'
      });
      return;
    }

    // Use the cleaned UUID
    saleId = cleanSaleId;
    console.log('Using sale ID for refund:', saleId);

    setRefundDialog({
      open: true,
      saleId: saleId,
      refundType: 'REFUND',
      reason: '',
      loading: false
    });
  };

  const handleRefundClose = () => {
    setRefundDialog(prev => ({
      ...prev,
      open: false,
      saleId: null,
      reason: '',
      loading: false
    }));
  };

  // Check if an activity has already been refunded
  const isAlreadyRefunded = (activity) => {
    // For activity log entries, check if this is a refund activity itself
    if (activity.Type === 'ACTIVITY_LOG') {
      return activity.ActionDescription?.includes('marked as refunded') ||
             activity.ActionDescription?.includes('marked as defective');
    }

    // For stock movements, check if this is already a refund transaction
    if (activity.Type === 'STOCK_MOVEMENT') {
      return activity.Notes?.toLowerCase().includes('refunded') ||
             activity.Notes?.toLowerCase().includes('defective') ||
             activity.MovementType === 'REFUND';
    }

    return false;
  };

  const handleRefundSubmit = async () => {
    if (!refundDialog.saleId || !refundDialog.reason.trim()) {
      console.log('Validation failed - saleId:', refundDialog.saleId, 'reason:', refundDialog.reason);
      setInfoModal({
        open: true,
        title: 'Validation Error',
        message: 'Please enter a reason for the refund.'
      });
      return;
    }

    // No minimum character requirement for reason

    // Since the API lookup worked, we trust the sale ID is valid
    const finalSaleId = refundDialog.saleId.trim();

    console.log('=== DEBUG REFUND SUBMISSION ===');
    console.log('Sale ID for refund:', finalSaleId);
    console.log('Refund data:', {
      RefundType: refundDialog.refundType,
      Reason: refundDialog.reason.trim()
    });

    setRefundDialog(prev => ({ ...prev, loading: true }));

    try {
      const response = await sales.refund(finalSaleId, {
        RefundType: refundDialog.refundType,
        Reason: refundDialog.reason.trim()
      });

      console.log('Refund response:', response);

      // Refresh the activities list
      fetchActivities();
      handleRefundClose();

      // Show success modal
      setSuccessModal({
        open: true,
        message: 'Refund processed successfully!'
      });
    } catch (error) {
      console.error('Error refunding sale:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error URL:', error.config?.url);

      // Show user-friendly error modal
      const errorMsg = error.response?.data?.Error || error.message;
      setErrorModal({
        open: true,
        title: 'Refund Error',
        message: errorMsg
      });
    } finally {
      setRefundDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const getActivityIcon = (type, entityType) => {
    // For stock movements
    if (entityType === 'STOCK') {
      switch (type) {
        case 'IN':
          return <AddIcon />;
        case 'OUT':
          return <RemoveIcon />;
        case 'TRANSFER':
          return <TransferIcon />;
        case 'SALE':
          return <SaleIcon />;
        case 'ADJUSTMENT':
          return <AdjustmentIcon />;
        default:
          return <HistoryIcon />;
      }
    }

    // For general activities
    switch (type) {
      case 'PRODUCT_CREATE':
        return <CreateIcon />;
      case 'PRODUCT_UPDATE':
        return <UpdateIcon />;
      case 'PRODUCT_DELETE':
        return <DeleteIcon />;
      case 'LOOKUP_CREATE':
        return <CreateIcon />;
      case 'LOOKUP_UPDATE':
        return <UpdateIcon />;
      case 'LOOKUP_DELETE':
        return <DeleteIcon />;
      case 'SALE_REFUND':
        return <span style={{color: '#666', fontSize: '14px'}}>Void</span>;
      default:
        return <HistoryIcon />;
    }
  };

  // Keep the old function for backward compatibility
  const getMovementIcon = (type) => {
    return getActivityIcon(type, 'STOCK');
  };

  const getActivityColor = (type, entityType) => {
    // For stock movements
    if (entityType === 'STOCK') {
      switch (type) {
        case 'IN':
          return theme.palette.success.main;
        case 'OUT':
          return theme.palette.error.main;
        case 'TRANSFER':
          return theme.palette.info.main;
        case 'SALE':
          return theme.palette.warning.main;
        case 'ADJUSTMENT':
          return theme.palette.secondary.main;
        default:
          return theme.palette.text.secondary;
      }
    }

    // For general activities
    switch (type) {
      case 'PRODUCT_CREATE':
      case 'LOOKUP_CREATE':
        return theme.palette.success.main;
      case 'PRODUCT_UPDATE':
      case 'LOOKUP_UPDATE':
        return theme.palette.info.main;
      case 'PRODUCT_DELETE':
      case 'LOOKUP_DELETE':
        return theme.palette.error.main;
      case 'SALE_REFUND':
        return theme.palette.warning.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getActivityLabel = (type, entityType) => {
    // For stock movements
    if (entityType === 'STOCK') {
      switch (type) {
        case 'IN':
          return 'Stock In';
        case 'OUT':
          return 'Stock Out';
        case 'TRANSFER':
          return 'Transfer';
        case 'SALE':
          return 'Sale';
        case 'ADJUSTMENT':
          return 'Adjustment';
        default:
          return type;
      }
    }

    // For general activities
    switch (type) {
      case 'PRODUCT_CREATE':
        return 'Product Created';
      case 'PRODUCT_UPDATE':
        return 'Product Updated';
      case 'PRODUCT_DELETE':
        return 'Product Deleted';
      case 'LOOKUP_CREATE':
        return 'Lookup Created';
      case 'LOOKUP_UPDATE':
        return 'Lookup Updated';
      case 'LOOKUP_DELETE':
        return 'Lookup Deleted';
      case 'SALE_REFUND':
        return 'Sale Refunded';
      default:
        return type.replace(/_/g, ' ');
    }
  };

  // Keep old functions for backward compatibility
  const getMovementColor = (type) => {
    return getActivityColor(type, 'STOCK');
  };

  const getMovementLabel = (type) => {
    return getActivityLabel(type, 'STOCK');
  };

  const formatQuantityChange = (type, quantity, previousQty, newQty) => {
    const sign = type === 'IN' ? '+' : type === 'OUT' || type === 'SALE' ? '-' : '';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {sign}{Math.abs(quantity)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          ({previousQty} → {newQty})
        </Typography>
      </Box>
    );
  };

  // Mobile Activity Card Component
  const ActivityCard = ({ activity }) => {
    const activityKey = `${activity.Type}-${activity.Id}`;
    const isExpanded = expandedRows[activityKey];
    const hasPrice = activity.Type === 'STOCK_MOVEMENT' && (activity.WholesalePrice || activity.RetailPrice);

    return (
      <Card
        sx={{
          mb: 2,
          background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
          border: '1px solid rgba(0, 191, 255, 0.2)',
          '&:hover': {
            border: '1px solid rgba(0, 191, 255, 0.4)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                mr: 2,
                backgroundColor: `${getActivityColor(activity.ActivityType || activity.MovementType, activity.EntityType)}20`,
                color: getActivityColor(activity.ActivityType || activity.MovementType, activity.EntityType),
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              {activity.DisplayTitle?.substring(0, 2) || activity.EntityType?.substring(0, 2) || 'AC'}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {activity.DisplayTitle || 'Unknown Entity'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  icon={getActivityIcon(activity.ActivityType || activity.MovementType, activity.EntityType)}
                  label={getActivityLabel(activity.ActivityType || activity.MovementType, activity.EntityType)}
                  size="small"
                  sx={{
                    backgroundColor: `${getActivityColor(activity.ActivityType || activity.MovementType, activity.EntityType)}20`,
                    color: getActivityColor(activity.ActivityType || activity.MovementType, activity.EntityType),
                    fontWeight: 600,
                    height: 20,
                    fontSize: '0.7rem',
                  }}
                />
                <Chip
                  label={activity.EntityType || 'System'}
                  size="small"
                  color="secondary"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
            {hasPrice && (
              <IconButton
                size="small"
                onClick={() => toggleRowExpansion(activityKey)}
                sx={{ ml: 1 }}
              >
                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
                        {/* Void button - show for any refundable activity - mobile */}
            {canRefundSales() && !isAlreadyRefunded(activity) && (
              <Tooltip title="VOID">
                <IconButton
                  size="small"
                  onClick={() => handleRefundClick(activity)}
                  sx={{
                    ml: 1,
                    color: 'info.main',
                    '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.1)' }
                  }}
                >
                  <span style={{fontSize: '12px'}}>Void</span>
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">
                Details
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {activity.Type === 'STOCK_MOVEMENT' ? (activity.BranchName || 'Unknown Branch') : activity.DisplayDescription}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">
                Date & Time
              </Typography>
              <Typography variant="body2" fontWeight={600} color="primary">
                {formatManilaTime(activity.CreatedAt)?.date || '-'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {formatManilaTime(activity.CreatedAt)?.time || '-'}
              </Typography>
            </Grid>
          </Grid>

          {activity.Type === 'STOCK_MOVEMENT' && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  Quantity Change:
                </Typography>
                <Box>
                  {formatQuantityChange(
                    activity.MovementType,
                    activity.Quantity,
                    activity.PreviousQuantity,
                    activity.NewQuantity
                  )}
                </Box>
              </Box>
              {activity.RetailPrice && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Retail Price:
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    ₱{activity.RetailPrice.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {(activity.Notes || activity.ActionDescription) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Notes: {activity.Notes || activity.ActionDescription}
              </Typography>
            </Box>
          )}

          {activity.Reference && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Reference: {activity.Reference}
              </Typography>
            </Box>
          )}

          {hasPrice && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0, 191, 255, 0.05)', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#00BFFF' }}>
                  Pricing Details
                </Typography>
                <Grid container spacing={2}>
                  {activity.WholesalePrice && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Capital Price:
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        ₱{activity.WholesalePrice.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                  {activity.RetailPrice && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Retail Price:
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="primary">
                        ₱{activity.RetailPrice.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Collapse>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Activity Log
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Track all system activities including product management, stock movements, and lookup operations
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={isMobile ? 10 : 3}>
              <TextField
                fullWidth
                placeholder="Search products or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: isMobile && (
                    <IconButton
                      onClick={() => setShowFilters(!showFilters)}
                      size="small"
                      sx={{ color: showFilters ? 'primary.main' : 'text.secondary' }}
                    >
                      <FilterIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            {isMobile && (
              <Grid item xs={2}>
                <Tooltip title="Clear filters">
                  <IconButton onClick={clearFilters} size="large">
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {!isMobile && (
              <>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Activity Type</InputLabel>
                    <Select
                      value={activityType}
                      label="Activity Type"
                      onChange={(e) => setActivityType(e.target.value)}
                    >
                      <MenuItem value="">All Activities</MenuItem>
                      <MenuItem value="IN">Stock In</MenuItem>
                      <MenuItem value="OUT">Stock Out</MenuItem>
                      <MenuItem value="TRANSFER">Transfer</MenuItem>
                      <MenuItem value="SALE">Sale</MenuItem>
                      <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                      <MenuItem value="PRODUCT_CREATE">Product Create</MenuItem>
                      <MenuItem value="PRODUCT_UPDATE">Product Update</MenuItem>
                      <MenuItem value="PRODUCT_DELETE">Product Delete</MenuItem>
                      <MenuItem value="LOOKUP_CREATE">Lookup Create</MenuItem>
                      <MenuItem value="LOOKUP_UPDATE">Lookup Update</MenuItem>
                      <MenuItem value="LOOKUP_DELETE">Lookup Delete</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={clearFilters}
                    startIcon={<ClearIcon />}
                    sx={{
                      color: '#1a237e',
                      borderColor: 'grey.300',
                      '&:hover': {
                        borderColor: 'grey.500',
                        backgroundColor: 'grey.50'
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Grid>
              </>
            )}
            <Collapse in={isMobile && showFilters} sx={{ width: '100%' }}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Activity Type</InputLabel>
                    <Select
                      value={activityType}
                      label="Activity Type"
                      onChange={(e) => setActivityType(e.target.value)}
                    >
                      <MenuItem value="">All Activities</MenuItem>
                      <MenuItem value="IN">Stock In</MenuItem>
                      <MenuItem value="OUT">Stock Out</MenuItem>
                      <MenuItem value="TRANSFER">Transfer</MenuItem>
                      <MenuItem value="SALE">Sale</MenuItem>
                      <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                      <MenuItem value="PRODUCT_CREATE">Product Create</MenuItem>
                      <MenuItem value="PRODUCT_UPDATE">Product Update</MenuItem>
                      <MenuItem value="PRODUCT_DELETE">Product Delete</MenuItem>
                      <MenuItem value="LOOKUP_CREATE">Lookup Create</MenuItem>
                      <MenuItem value="LOOKUP_UPDATE">Lookup Update</MenuItem>
                      <MenuItem value="LOOKUP_DELETE">Lookup Delete</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </CardContent>
      </Card>

      {/* Activity Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading activity log...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          ) : (
            <>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer>
                  <Table sx={{ tableLayout: 'auto' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '50px' }} />
                        <TableCell>Entity</TableCell>
                        <TableCell>Activity Type</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Quantity</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Retail Price</TableCell>
                        <TableCell>Reference</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Refund Reason</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Refunded Date</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Date & Time</TableCell>
                        <TableCell sx={{ width: '80px' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activities.map((activity) => {
                        const activityKey = `${activity.Type}-${activity.Id}`;
                        const isExpanded = expandedRows[activityKey];
                        return (
                          <React.Fragment key={activityKey}>
                            <TableRow hover sx={{
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                  '& td': {
                                    verticalAlign: 'middle',
                                    py: 1.5 // Consistent padding for all cells
                                  }
                                }}>
                              <TableCell sx={{ verticalAlign: 'middle' }}>
                                {activity.Type === 'STOCK_MOVEMENT' && (activity.WholesalePrice || activity.RetailPrice) ? (
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRowExpansion(activityKey)}
                                  >
                                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                ) : null}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      mr: 2,
                                      backgroundColor: `${getActivityColor(activity.ActivityType || activity.MovementType, activity.EntityType)}20`,
                                      color: getActivityColor(activity.ActivityType || activity.MovementType, activity.EntityType),
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {activity.DisplayTitle?.substring(0, 2) || activity.EntityType?.substring(0, 2) || 'AC'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                      <TruncatedText text={activity.DisplayTitle || 'Unknown Entity'} maxLength={20} title="Entity Name" />
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {activity.EntityType || 'System'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={getActivityIcon(activity.ActivityType || activity.MovementType, activity.EntityType)}
                                  label={getActivityLabel(activity.ActivityType || activity.MovementType, activity.EntityType)}
                                  size="small"
                                  sx={{
                                    backgroundColor: `${getActivityColor(activity.ActivityType || activity.MovementType, activity.EntityType)}20`,
                                    color: getActivityColor(activity.ActivityType || activity.MovementType, activity.EntityType),
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {activity.Type === 'STOCK_MOVEMENT' ? (
                                    <TruncatedText text={activity.BranchName || 'Unknown Branch'} maxLength={20} title="Branch Name" />
                                  ) : (
                                    <TruncatedText text={activity.DisplayDescription} maxLength={25} title="Activity Details" />
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {activity.Type === 'STOCK_MOVEMENT' ? (
                                  formatQuantityChange(
                                    activity.MovementType,
                                    activity.Quantity,
                                    activity.PreviousQuantity,
                                    activity.NewQuantity
                                  )
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    {activity.NewValues ? 'Updated' : activity.OldValues ? 'Deleted' : 'Created'}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {activity.Type === 'STOCK_MOVEMENT' && activity.RetailPrice ? (
                                  <Typography variant="body2" fontWeight={600} color="primary">
                                    ₱{activity.RetailPrice.toFixed(2)}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="textSecondary">
                                  <TruncatedText
                                    text={activity.Reference || '-'}
                                    maxLength={20}
                                    title="Reference"
                                  />
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 200 }}>
                                  <TruncatedText
                                    text={activity.Notes || activity.ActionDescription || '-'}
                                    maxLength={20}
                                    title="Notes"
                                  />
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" color="textSecondary">
                                  {/* Use real database Status field */}
                                  {activity.Status || (activity.Type === 'STOCK_MOVEMENT' ? 'ACTIVE' : '-')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 150 }}>
                                  <TruncatedText
                                    text={activity.RefundReason || '-'}
                                    maxLength={20}
                                    title="Refund Reason"
                                  />
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" color="textSecondary">
                                  {/* Use real database RefundedAt field - now in Manila time */}
                                  {formatManilaTime(activity.RefundedAt)?.date || '-'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {formatManilaTime(activity.RefundedAt)?.time || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" fontWeight={500}>
                                  {formatManilaTime(activity.CreatedAt)?.date || '-'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {formatManilaTime(activity.CreatedAt)?.time || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                                                {/* Void button - show for any refundable activity */}
                                {canRefundSales() && !isAlreadyRefunded(activity) && (
                                  <Tooltip title="VOID">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRefundClick(activity)}
                                      sx={{
                                        color: 'info.main',
                                        '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.1)' }
                                      }}
                                    >
                                      <span style={{fontSize: '12px'}}>Void</span>
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                            {activity.Type === 'STOCK_MOVEMENT' && (activity.WholesalePrice || activity.RetailPrice) && (
                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={13}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ margin: 2, p: 2, bgcolor: 'rgba(0, 191, 255, 0.05)', borderRadius: 1 }}>
                                      <Typography variant="h6" gutterBottom component="div" sx={{ color: '#00BFFF' }}>
                                        Pricing Details
                                      </Typography>
                                      <Grid container spacing={2}>
                                        {activity.WholesalePrice && (
                                          <Grid item xs={6}>
                                            <Typography variant="body2" color="textSecondary">
                                              Capital Price:
                                            </Typography>
                                            <Typography variant="h6" fontWeight={600}>
                                              ₱{activity.WholesalePrice.toFixed(2)}
                                            </Typography>
                                          </Grid>
                                        )}
                                        {activity.RetailPrice && (
                                          <Grid item xs={6}>
                                            <Typography variant="body2" color="textSecondary">
                                              Retail Price:
                                            </Typography>
                                            <Typography variant="h6" fontWeight={600} color="primary">
                                              ₱{activity.RetailPrice.toFixed(2)}
                                            </Typography>
                                          </Grid>
                                        )}
                                      </Grid>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
                <Grid container spacing={2}>
                  {activities.map((activity) => (
                    <Grid item xs={12} key={`${activity.Type}-${activity.Id}`}>
                      <ActivityCard activity={activity} />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: 1, borderColor: 'divider' }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Refund Dialog */}
      <Dialog open={refundDialog.open} onClose={handleRefundClose} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Sale as Refunded or Defective</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Refund Type:
            </Typography>
            <RadioGroup
              row
              value={refundDialog.refundType}
              onChange={(e) => setRefundDialog(prev => ({ ...prev, refundType: e.target.value }))}
            >
              <FormControlLabel value="REFUND" control={<Radio />} label="Refund" />
              <FormControlLabel value="DEFECTIVE" control={<Radio />} label="Defective" />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            label="Reason"
            multiline
            rows={3}
            value={refundDialog.reason}
            onChange={(e) => setRefundDialog(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="Please provide a reason for this refund or defective item..."
            variant="outlined"
            helperText="Please provide a reason for this refund"
            error={false}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRefundClose} disabled={refundDialog.loading}>
            Cancel
          </Button>
          <Button
            onClick={handleRefundSubmit}
            variant="contained"
            color="warning"
            disabled={!refundDialog.reason.trim() || refundDialog.loading}
          >
            {refundDialog.loading ? 'Processing...' : 'Confirm Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={successModal.open} onClose={() => setSuccessModal({ open: false, message: '' })}>
        <DialogTitle sx={{ color: 'success.main' }}>✓ Success</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {successModal.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSuccessModal({ open: false, message: '' })}
            variant="contained"
            color="success"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Modal */}
      <Dialog open={errorModal.open} onClose={() => setErrorModal({ open: false, title: '', message: '' })}>
        <DialogTitle sx={{ color: 'error.main' }}>⚠ {errorModal.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {errorModal.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setErrorModal({ open: false, title: '', message: '' })}
            variant="contained"
            color="error"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Modal */}
      <Dialog open={infoModal.open} onClose={() => setInfoModal({ open: false, title: '', message: '' })}>
        <DialogTitle sx={{ color: 'info.main' }}>ℹ️ {infoModal.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {infoModal.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInfoModal({ open: false, title: '', message: '' })}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Text Modal for viewing full text */}
      <Dialog
        open={textModal.open}
        onClose={() => setTextModal({ open: false, title: '', text: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: 'primary.main' }}>{textModal.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
            {textModal.text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTextModal({ open: false, title: '', text: '' })}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ActivityLog;